<template>
    <div class="whitelabel-frequently-asked-questions">
        <div class="main-heading">
            <h3>Frequently Asked Questions</h3>
        </div>

        <div class="main-content">
            <div class="description">
                <p>To help you manage your booking with confidence, we've answered some of the most frequently asked questions about changing bookings, vehicle details, payments and account support below.</p>
                <p>Simply click on a question to view the answer. If you can't find what you're looking for, please contact us and we'll be happy to help.</p>
            </div>

            <div class="dropdown-wrapper">
                <drop-down-show-info
                    v-for="faqContent in faqContents"
                    :key="faqContent.title"
                    :title="faqContent.title"
                    :content="faqContent.content"
                    :show-content="faqContent.showContent"
                />
            </div>

            <div class="description">
                <p>Still need help?</p>
                <p class="paragraph-with-link">
                    If your query relates to parking charges or enforcement please visit our dedicated FAQ page
                    <a href="https://horizonparking.co.uk/parking-charge-faqs/" class="horizon-parking-link">Parking Charge FAQs – Horizon Parking</a>
                </p>
                <p class="paragraph-with-link">
                    For anything else, feel free to contact us at
                    <a href="mailto:spaces@horizonparking.co.uk" class="contact-email-link">spaces@horizonparking.co.uk</a>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import DropDownShowInfo from './DropDownShowInfo';

    export default {
        name: 'whitelabel-frequently-asked-questions',

        components: { DropDownShowInfo },

        computed: {
            /**
             * Get the FAQ content.
             *
             * @returns {Array}
             */
            faqContents() {
                return [
                    {
                        title: 'Changing Your Booking Times (Hourly/Daily Bookings)',
                        content: `
                            <p>You can amend <strong>upcoming</strong> and <strong>active</strong> bookings at any time before they are completed. This includes:</p>
                            <br/>
                            <ul>
                                <li>Changing the <strong>vehicle registration</strong></li>
                                <li>Extending your <strong>end date/time</strong></li>
                            </ul>
                            <br/>
                            <p>Please note:</p>
                            <p><strong>Completed bookings cannot be amended.</strong></p>
                            <br/>
                            <p><strong>How to Change your booking</strong></p>
                            <br/>
                            <ol>
                                <li>Log in to your account via the Horizon Spaces app.</li>
                                <li>Tap <strong>Bookings</strong> in the bottom-left menu and select <strong>Active</strong> or <strong>Upcoming</strong>, depending on the booking you wish to change.</li>
                                <li>Select the booking and tap <strong>Manage Booking</strong>.</li>
                                <li>Scroll to <strong>Booking Details</strong> and select <strong>Change date & time</strong> under the start/end times you wish to amend.</li>
                                <li>Choose your new times and complete any additional payment required to confirm the change.</li>
                            </ol>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'Amending Your Vehicle Registration',
                        content: `
                            <p>You can change the vehicle on your booking at any point before the booking is complete.</p>
                            <br/>
                            <p>Please be aware:</p>
                            <br/>
                            <ul>
                                <li>Only the vehicle listed on the booking is permitted to park in the space.</li>
                                <li>You must ensure the <strong>correct vehicle registration is entered before entering the car park</strong> to avoid receiving a Parking Charge</li>
                            </ul>
                            <br/>
                            <p><strong>How to change your vehicle</strong></p>
                            <br/>
                            <ol>
                                <li>Log in to your account.</li>
                                <li>Select <strong>Active Bookings</strong> from the menu.</li>
                                <li>Choose the booking you wish to update and tap <strong>Manage Booking</strong>.</li>
                                <li>Scroll to <strong>Booking Details</strong> and tap <strong>Change vehicle</strong>.</li>
                                <li>Select a saved vehicle or add a new one, then tap <strong>Submit</strong> to save your changes.</li>
                            </ol>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'Why can’t  I change my arrival date or pre-book in advance?',
                        content: `
                            <p>If the app or website does not allow you to change your arrival date to a future date or make a booking ahead of time, it is likely because you are trying to pre-book at a <strong>pay-on-arrival</strong> location.</p>
                            <br/>
                            <p>Some car parks do not allow pre-booking to prevent customers from booking when there may be no available space.</p>
                            <p>You can still book once you have arrived and are parked in a valid space.</p>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'The app/website says the car park is fully booked, but I am parked in a space. Why?',
                        content: `
                            <p>We do not always have access to the full car park capacity for daily or monthly parking. To avoid affecting other users, we may only have limited spaces available.</p>
                            <br/>
                            <p>If the system shows “fully booked”, please try searching for an alternative nearby location.</p>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'I cannot make payment after I have parked or pay retrospectively for my stay?',
                        content: `
                            <p>Backdated/post-pay bookings are <strong>not available at all car parks</strong> and are only supported at selected locations.</p>
                            <p>If you believe your location should allow post-pay and you are experiencing issues, please contact us at:</p>
                            <p><a href="mailto:spaces@horizonparking.co.uk">spaces@horizonparking.co.uk</a></p>
                            <br/>
                            <p>please ensure you provide details of the location and the issue you are experiencing.</p>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'Can I block-book or pay for multiple spaces?',
                        content: `
                            <p>Yes, we can arrange this for you.</p>
                            <p>To set this up, please email us with the following information:</p>
                            <br/>
                            <ul>
                                <li>Car park name</li>
                                <li>Number of spaces required</li>
                                <li>Duration of booking</li>
                            </ul>
                            <br/>
                            <p><a href="mailto:spaces@horizonparking.co.uk">spaces@horizonparking.co.uk</a></p>
                        `,
                        showContent: false,
                    },
                    {
                        title: 'How do I close my account?',
                        content: `
                            <p>To close your account, please email us at:</p>
                            <p><a href="mailto:spaces@horizonparking.co.uk">spaces@horizonparking.co.uk</a></p>
                            <br/>
                            <p>Please note:</p>
                            <p>We are unable to close your account if you have an <strong>active booking</strong>. Account closure can only be processed once all bookings are completed.</p>
                        `,
                        showContent: false,
                    },
                ];
            },
        },
    };
</script>

<style lang="less" scoped>
    .whitelabel-frequently-asked-questions {
        p  {
            color: #000000;
            font-weight: 400;
            font-size: 15px;
            line-height: 100%;
            letter-spacing: 0%;
            margin-bottom: 0;
        }

        .main-content {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }

        .description {
            display: flex;
            flex-direction: column;
            gap: 15px;

            .paragraph-with-link {
                display: flex;
                flex-direction: column;
            }
        }

        .dropdown-wrapper {
            margin-top: 10px;
            margin-bottom: 10px;
        }

        .horizon-parking-link {
            text-decoration: underline;
        }
    }
</style>
